import { useEffect, useState } from 'react';
import Loading from '../../components/loading/Loading';
import { useNavigate, useParams } from 'react-router-dom';
import { getMembershipById } from '../../api/packages';
import { getDrawInfoById } from '../../api/draws';
import { PackageFormData } from '../../@types/packageForm';
import { PackageCard } from '../../components/packageCard';

const PackageInfo = () => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { packageId } = useParams<{ packageId: string }>();
    const [thePackage, setThePackage] = useState<PackageFormData | null>(null);
    const [drawData, setDrawData] = useState<any>(null);
    const [error, setError] = useState({
        message: '',
        state: false
    });
    const [selectedDrawDate, setSelectedDrawDate] = useState<string>('current');

    useEffect(() => {
        (async () => {
            if (packageId) {
                try {
                    setLoading(true);
                    setError({ message: '', state: false });
                    const response = await getMembershipById(packageId);
                    if (response.data) {
                        setThePackage(response?.data);
                        
                        // Fetch draw data if package has selectedDraw
                        const selectedDraw = (response.data as any)?.selectedDraw;
                        if (selectedDraw) {
                            try {
                                const drawResponse = await getDrawInfoById(selectedDraw);
                                setDrawData(drawResponse.data);
                            } catch (drawErr) {
                                console.warn('Failed to fetch draw data:', drawErr);
                                // Don't fail the whole page if draw fetch fails
                            }
                        }
                    } else {
                        setError({ message: 'There is no Package with this ID.', state: true });
                    }
                    setLoading(false);
                } catch (err: any) {
                    setError({ message: err.response.data.error, state: true });
                    setLoading(false);
                }
            }
        })();
    }, []);

    return (
        <div>
            {!error.state ? (
                loading ? (
                    <Loading />
                ) : (
                    <div className="flex flex-col items-center justify-center">
                        <h1 className="mb-5 w-full max-w-sm lg:max-w-[50%] text-left text-primary font-bold text-xl">
                            Package Info
                        </h1>
                        <div className="w-full max-w-sm lg:max-w-[50%]">
                            {thePackage?.tiers?.map((tier, index) => {
                                const hasCustomTerms = !!(thePackage as any)?.termsHtml && String((thePackage as any).termsHtml).trim().length > 0;
                                const termsLink = hasCustomTerms && packageId
                                    ? `/terms?membershipId=${packageId}`
                                    : '/terms';
                                // Compute current promo multiplier from membership promo periods
                                const promos = (thePackage as any)?.promoPeriods as Array<{ start: string; end: string; multiplier: number }> | undefined;
                                // Use selected timezone to determine active period; normalize both sides to timezone key
                                const tz = (thePackage as any)?.timezone as string | undefined;
                                const toKey = (val?: string) => {
                                    if (!val) return '';
                                    const s = String(val);
                                    if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) return s.slice(0, 16);
                                    try {
                                        const d = new Date(s);
                                        const formatted = new Intl.DateTimeFormat('sv-SE', {
                                            timeZone: tz || Intl.DateTimeFormat().resolvedOptions().timeZone,
                                            year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
                                        }).format(d);
                                        return formatted.replace(' ', 'T');
                                    } catch { return s.slice(0, 16); }
                                };
                                const nowKey = toKey(new Date().toISOString());
                                const activePromo = Array.isArray(promos)
                                    ? promos.find(p => toKey(p.start) <= nowKey && nowKey < toKey(p.end))
                                    : undefined;
                                const multiplier = activePromo?.multiplier ?? 1;
                                const baseEntries = Number(tier.recurringEntry ?? 0);
                                const totalEntries = baseEntries * multiplier;
                                return (
                                    <div key={index}>
                                        <PackageCard
                                            title={tier.name}
                                            price={tier.price}
                                            subtitle={`${tier.recurringEntry}`}
                                            numberOfTicket={`Number of Ticket: ${tier.numberOfTicket}`}
                                            frequency={`Frequency : ${tier.frequency}`}
                                            currency={tier.currency}
                                            drawDate={thePackage?.nextDrawIn}
                                            content={tier.highlight}
                                            deactivatedAt={tier.deactivatedAt}
                                            purchase={!tier.deactivatedAt}
                                            termsLink={termsLink}
                                            entriesPreview={
                                                <>
                                                    <span className="font-medium">You will get: {totalEntries} subscriptions</span> 
                                                    {multiplier > 1 && (
                                                        <div className="text-[11px] text-gray-500">
                                                            {baseEntries} × ({multiplier} Promo) = {totalEntries}
                                                        </div>
                                                    )}
                                                </>
                                            }
                                            purchaseFunc={() => {
                                                const totalCost = tier.price;
                                                const currency = tier.currency;
                                                const tireId = tier.id;
                                                const mode = 'package';
                                                navigate('/total', {
                                                    state: { mode, tireId, totalCost, currency }
                                                });
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>

                        {/* Draw Links Section */}
                        {drawData && (
                            <div className="w-full max-w-sm lg:max-w-[50%] mt-8">
                                <h2 className="mb-5 text-left text-primary font-bold text-xl">
                                    One-Time Draw Entry
                                </h2>
                                {/* Draw Date Selection Dropdown */}
                                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Select Draw Date
                                    </label>
                                    <select 
                                        value={selectedDrawDate}
                                        onChange={(e) => setSelectedDrawDate(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                                    >
                                        <option value="current">
                                            {drawData.runAt ? new Date(drawData.runAt).toLocaleDateString() : '...'} (Current Draw)
                                        </option>
                                        <option value="next">
                                            {thePackage?.nextDrawIn ? new Date(thePackage.nextDrawIn).toLocaleDateString() : '...'} (Next Draw)
                                        </option>
                                    </select>
                                </div>
                                
                                {/* Default Draw Card */}
                                <div className="mb-4">
                                    <PackageCard
                                        title={`${drawData.name} (Default)`}
                                        price={Number(drawData.entryCost)}
                                        numberOfTicket={drawData.ticketCap ? String(drawData.ticketCap) : ''}
                                        currency={drawData.currency}
                                        drawDate={selectedDrawDate === 'next' && drawData.nextDrawDate ? drawData.nextDrawDate : drawData.runAt}
                                        content={''}
                                        deactivatedAt={drawData.deactivatedAt}
                                        purchase={!drawData.deactivatedAt && new Date(drawData.runAt) > new Date()}
                                        termsLink="/terms"
                                        hideCancelAnytime={true}
                                        hideRecurringEntry={true}
                                        entriesPreview={
                                            <span className="font-medium">Single entry to draw</span>
                                        }
                                        purchaseFunc={() => {
                                            const totalCost = Number(drawData.entryCost);
                                            const currency = drawData.currency;
                                            const drawId = drawData.id;
                                            const mode = 'package-draw';
                                            const numTickets = 1;
                                            const chosenDrawDate = selectedDrawDate === 'next' && drawData.nextDrawDate ? drawData.nextDrawDate : drawData.runAt;
                                            navigate('/total', {
                                                state: { mode, drawId, totalCost, currency, numTickets, packageId, selectedDrawDate: chosenDrawDate }
                                            });
                                        }}
                                    />
                                </div>

                                {/* Draw Links Cards */}
                                {drawData.links && drawData.links.length > 0 && drawData.links.map((link: any, index: number) => (
                                    <div key={index} className="mb-4">
                                        <PackageCard
                                            title={`${drawData.name} (${link.name})`}
                                            price={link.entryCost !== null && link.entryCost !== undefined ? Number(link.entryCost) : Number(drawData.entryCost)}
                                            numberOfTicket={link.ticketCap !== null && link.ticketCap !== undefined ? String(link.ticketCap) : (drawData.ticketCap ? String(drawData.ticketCap) : '')}
                                            currency={link.currency !== null && link.currency !== undefined ? link.currency : drawData.currency}
                                            drawDate={selectedDrawDate === 'next' && drawData.nextDrawDate ? drawData.nextDrawDate : drawData.runAt}
                                            content={''}
                                            deactivatedAt={drawData.deactivatedAt || (!link.isActive ? 'inactive' : null)}
                                            purchase={!drawData.deactivatedAt && link.isActive !== false && new Date(drawData.runAt) > new Date()}
                                            termsLink="/terms"
                                            hideCancelAnytime={true}
                                            hideRecurringEntry={true}
                                            entriesPreview={
                                                <span className="font-medium">Single entry to draw via {link.name}</span>
                                            }
                                            purchaseFunc={() => {
                                                const totalCost = link.entryCost !== null && link.entryCost !== undefined ? Number(link.entryCost) : Number(drawData.entryCost);
                                                const currency = link.currency !== null && link.currency !== undefined ? link.currency : drawData.currency;
                                                const drawId = drawData.id;
                                                const drawLinkSlug = link.slug;
                                                const mode = 'package-draw';
                                                const numTickets = 1;
                                                const chosenDrawDate = selectedDrawDate === 'next' && drawData.nextDrawDate ? drawData.nextDrawDate : drawData.runAt;

                                                navigate('/total', {
                                                    state: { mode, drawId, drawLinkSlug, totalCost, currency, numTickets, packageId, selectedDrawDate: chosenDrawDate }
                                                });
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )
            ) : (
                <div className="text-lg font-normal text-neutral">
                    <p>{error.message}</p>
                </div>
            )}
        </div>
    );
};

export default PackageInfo;
